import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Mutex } from 'async-mutex'
import { ActiveClaimCheckStatus } from '@typesApp/ClaimRecord'
import { AuthTokenArgs, Customer, CustomerContext, WCTokens } from '@typesApp/Session'
import { HostSession } from '@typesApp/Environment'
import { config } from '@config/config'
import { clearLocalAuthToken, getLocalAuthToken, isLocalAuthTokenValid } from '@utils/authToken'
import { isCustomerNumberValid } from '@utils/isCustomerNumberValid'
import { activeClaimCheckStatusSelector, setActiveClaimRecord } from '@redux/slices/claimStatusSlice'
import { authTokenError, authTokenReady, connectorSetSessionInitializing } from '@redux/slices/connectorSlice'
import {
  useLazyGetCustomerContextByTokensQuery,
  useLazyGetCustomerContextQuery,
  useLazyGetSessionQuery,
} from '@redux/queries/sessions'
import { useLazyGetActiveClaimRecordQuery, useLazyRestrictedCheckQuery } from '@redux/queries/claimRecords'
import { useGetAuthTokenMutation } from '@redux/queries/auth'
import { useEnvironment } from './useEnvironment'

type UseSessionReturn = {
  /**
   * Fetch customer context data (with session id)
   */
  initContext: () => Promise<CustomerContext>

  /**
   * Fetch customer context data using provided wc tokens
   */
  initContextWithTokens: (tokens: WCTokens) => Promise<CustomerContext>

  /**
   * Fetch customer session and auth token if needed
   */
  initSession: (session: HostSession, customerNumber?: string) => Promise<void>
}

const mutex = new Mutex()

/**
 * Initialize the session by fetching the commerce customer session and auth token if needed
 */
export const useSession = (): UseSessionReturn => {
  const dispatch = useDispatch()
  const { anonymousUserId } = useEnvironment()
  const [fetchContext] = useLazyGetCustomerContextQuery()
  const [fetchContextByTokens] = useLazyGetCustomerContextByTokensQuery()
  const [fetchSession] = useLazyGetSessionQuery()
  const [fetchAuthToken] = useGetAuthTokenMutation()
  const [fetchRestrictedStatus] = useLazyRestrictedCheckQuery()
  const [fetchActiveClaimRecord] = useLazyGetActiveClaimRecordQuery()
  const activeClaimCheckStatus = useSelector(activeClaimCheckStatusSelector)

  const initContext = useCallback(async (): Promise<CustomerContext> => {
    try {
      return await fetchContext().unwrap()
    } catch (error) {
      console.warn('directbilling session - unable to get customer context data', error)
      return { sessionId: '', isPartiallyAuthenticated: false }
    }
  }, [fetchContext])

  const initContextWithTokens = useCallback(
    async (tokens: WCTokens): Promise<CustomerContext> => {
      try {
        return await fetchContextByTokens(tokens).unwrap()
      } catch (error) {
        console.warn('directbilling session - unable to get customer context data', error)
        return { sessionId: '', isPartiallyAuthenticated: false }
      }
    },
    [fetchContextByTokens]
  )

  const initCustomerActiveData = useCallback(
    async (customer: Customer) => {
      console.debug('directbilling session - init active data customerNumber=%s', customer?.id)

      if (!isCustomerNumberValid(customer?.id, anonymousUserId)) {
        console.warn('directbilling session - unable to init active data, customerNumber %s is not valid', customer?.id)
        return
      }

      fetchRestrictedStatus(customer?.id)

      const activeData = await fetchActiveClaimRecord({
        customerNumber: customer.id,
      }).unwrap()

      dispatch(setActiveClaimRecord(activeData?.id))
    },
    [dispatch, fetchActiveClaimRecord, fetchRestrictedStatus, anonymousUserId]
  )

  const initToken = useCallback(
    async (sessionId: string, customer: Customer): Promise<void> => {
      if (!isCustomerNumberValid(customer?.id, anonymousUserId)) {
        console.warn('directbilling session - cannot fetch auth token, customerNumber %s is not valid', customer?.id)
        throw new Error(`Cannot init auth token, invalid customer number ${customer?.id}`)
      }

      const args: AuthTokenArgs = {
        customerNumber: customer.id,
        customerEmail: customer.email ?? '',
        sessionId,
      }

      // remove sessionId from payload if is optional
      if (!sessionId || !sessionId?.trim()?.length) {
        delete args.sessionId
      }

      // fetch token first then initialize active data and check restricted status
      // note: fetch token will retry up to 5 times with backoff on 409
      const authToken = await fetchAuthToken(args).unwrap()
      if (authToken) {
        // Note: auth token API always returns an internal sessionId. If commerce
        // provides a session id, this internal sessionId has the same value.
        dispatch(authTokenReady({ authTokenSessionId: authToken.sessionId }))
        dispatch(setActiveClaimRecord(authToken?.claimRecordId))

        console.debug(
          'directbilling session - token initialized (sessionId=%s customerNumber=%s)',
          authToken.sessionId,
          customer.id
        )
      }

      fetchRestrictedStatus(customer.id)
    },
    [dispatch, fetchRestrictedStatus, fetchAuthToken, anonymousUserId]
  )

  const initSession = useCallback(
    async (hostSession: HostSession): Promise<void> => {
      console.debug('directbilling session - init auth token and active data')

      const { sessionId } = hostSession
      const disableCheck = config.getFeatures()?.disableAuthTokenSessionIdCheck

      if (!disableCheck && !sessionId) {
        console.warn('directbilling session - got empty sessionId, cannot fetch token')
        dispatch(authTokenError())
        return
      }

      dispatch(connectorSetSessionInitializing(true))

      try {
        await mutex.waitForUnlock()

        // fetch auth token
        if (!mutex.isLocked()) {
          const releaseMutex = await mutex.acquire()
          try {
            // fetch customer data
            const sessionData = await fetchSession(sessionId).unwrap()
            const { customer } = sessionData
            const customerNumber = customer.id

            // check again if token already initialized
            const localToken = getLocalAuthToken()
            if (isLocalAuthTokenValid(localToken, sessionId, customerNumber)) {
              dispatch(authTokenReady({ authTokenSessionId: localToken.sessionId }))
              console.debug(
                'directbilling session - token already initialized (sessionId=%s customerNumber=%s)',
                localToken.sessionId,
                customerNumber
              )

              // skip active data check if already completed
              if (activeClaimCheckStatus !== ActiveClaimCheckStatus.COMPLETE) {
                initCustomerActiveData(customer)
              }
              return
            }

            await initToken(sessionId, customer)
          } catch (error) {
            console.warn('directbilling session - error fetching auth token', error)
            clearLocalAuthToken()
            dispatch(authTokenError())
          } finally {
            releaseMutex()
          }
        }
      } finally {
        dispatch(connectorSetSessionInitializing(false))
      }
    },
    [dispatch, fetchSession, initCustomerActiveData, initToken, activeClaimCheckStatus]
  )

  return {
    initContext,
    initSession,
    initContextWithTokens,
  }
}
